import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Image,
  Button,
  Spinner,
} from "react-bootstrap";
import { useNotification } from "../../components/notification";
import apiService from "../../services/apiService";
import { useAuth } from "../../contexts/authContext";
import { cartSession } from "../../utils/cart";
import logo from "../../logo.png";

const LoginWithOTP = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { login } = useAuth();

  const phoneNumber = location.state?.phone;
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [countdown, setCountdown] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  //const inputRefs = Array.from({ length: 4 }, () => useRef(null));
  const inputRef0 = useRef(null);
  const inputRef1 = useRef(null);
  const inputRef2 = useRef(null);
  const inputRef3 = useRef(null);
  const inputRefs = [inputRef0, inputRef1, inputRef2, inputRef3];

  useEffect(() => {
    if (!phoneNumber) navigate("/login");
  }, [phoneNumber, navigate]);

  useEffect(() => {
    startCountdown();
  }, []);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const startCountdown = () => setCountdown(30);

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) inputRefs[index + 1].current.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs[index - 1].current.focus();
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsResending(true);
      await apiService.post("/otp/send-phoneOTP", { phone: phoneNumber });
      showNotification("success", "OTP resent successfully!", 3000);
      startCountdown();
    } catch (error) {
      showNotification("danger", error.message || "Failed to resend OTP!", 5000);
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 4) {
      showNotification("danger", "Please enter a 4-digit OTP", 5000);
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiService.post("/otp/login-with-phone-number", {
        phoneNumber,
        otp: otpString,
      });

      if (response.success) {
        const { token, refreshToken, user } = response.data;
        login({ token, refreshToken, user });

        if (cartSession.isExist()) {
          try {
            await apiService.post("/cart/merge", {
              sessionId: cartSession.get(),
            });
          } catch (error) {
            console.error("Cart sync failed", error);
          }
        }

        const redirectTo = location.state?.from || "/orders";
        navigate(redirectTo);
      } else {
        showNotification("danger", response.message, 5000);
      }
    } catch (error) {
      showNotification("danger", error.message || "OTP verification failed!", 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const maskedPhone = phoneNumber
    ? phoneNumber.slice(0, -4).replace(/./g, "*") + phoneNumber.slice(-4)
    : "";

  return (
    <Container className="min-vh-100 d-flex align-items-center justify-content-center">
      <Row className="w-100 justify-content-center">
        <Col xs={12} className="text-center mb-4">
          <Image src={logo} alt="Portal Logo" fluid width="225px" />
        </Col>
        <Col xs={11} md={8} lg={6} xl={5}>
          <Card className="p-4 shadow border-0 mt-4">
            <Card.Body>
              <h4 className="text-center mb-3">Verify Phone Number</h4>
              <p className="text-center text-muted mb-4">
                Enter the 4-digit OTP sent to your phone ending with <b>{maskedPhone}</b>
              </p>

              <div className="d-flex justify-content-center mb-3">
                {otp.map((digit, index) => (
                  <Form.Control
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    ref={inputRefs[index]}
                    className="text-center mx-2 fs-4"
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "0.5rem",
                      border: "1px solid #ced4da",
                    }}
                  />
                ))}
              </div>

              <div className="text-center mb-2">
                <Button
                  variant="link"
                  className="text-decoration-none text-primary"
                  onClick={() => navigate("/login", { state: { phone: phoneNumber } })}
                >
                  Change Phone Number
                </Button>
              </div>

              <div className="text-center mb-2">
                {countdown > 0 ? (
                  <Button variant="link" className="text-muted text-decoration-none">Resend available in <b>{countdown}s</b></Button>
                ) : (
                  <Button
                    variant="link"
                    className="text-decoration-none text-primary"
                    onClick={handleResendOTP}
                    disabled={isResending}
                  >
                    {isResending ? "Resending..." : "Resend OTP"}
                  </Button>
                )}
              </div>

              <div className="d-grid">
                <Button
                  variant="primary"
                  onClick={handleVerify}
                  disabled={isLoading}
                  className="btn-main"
                >
                  {isLoading ? (
                    <Spinner animation="border" role="status" size="sm">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  ) : (
                    "VERIFY & LOGIN"
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginWithOTP;
